import React, { useEffect, useMemo, useRef, useState } from "react"

import { type SerializedStyles, css } from "@emotion/react"
// @ts-ignore
import ReactIsCapsLockActive from "@matsun/reactiscapslockactive"
import { Input } from "antd"
import MaskedInput from "antd-mask-input"
import type {
    InputMaskOptions,
    MaskType,
} from "antd-mask-input/build/main/lib/MaskedInput"
import awesomeDebouncePromise from "awesome-debounce-promise"
import {
    type Control,
    Controller,
    type DeepMap,
    type FieldError,
    type FieldValues,
    UseFormSetValue,
} from "react-hook-form"
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai"

import type { IIndexable } from "@common-shared/types/general.types.ts"

import type { IPasswordValidationRule } from "@front-shared/types/objects.types.ts"

import { NIRVANA_COLORS } from "../config/theme.config.ts"
import { FRONT_PASSWORD_VALIDATION_CONFIG } from "../config/validation.config"
import { useIsInputAutofilled } from "../hooks/isInputAutofilled.hook"
import { useScrollErroredInputIntoView } from "../hooks/scrollErroredInputtIntoView.hook"
import { FloatingLabelElement } from "./floatingLabel.element"
import { InputErrorElement } from "./inputError.element"
import { PasswordStrengthElement } from "./passwordStrength.element"
import { PrimaryText } from "./primaryText.element"

export const InputElement: React.FunctionComponent<
    IInputElementProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)

    const [currentInputValue, setCurrentInputValue] = useState<string>()

    useScrollErroredInputIntoView(
        props.name,
        containerRef,
        props.reactHookErrors,
        props.ignoreScrollIntoViewOnError,
    )

    const isAutoFilled = useIsInputAutofilled(containerRef)

    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)

    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    const debouncedOnChange = useMemo(() => {
        if (!props.onChange) {
            return null
        }

        return awesomeDebouncePromise(props.onChange, props.debounce || 0)
    }, [props.onChange])

    const managedInputType: typeof props.type = useMemo(() => {
        return props.type === "password" &&
            !props.ignorePasswordVisibilityToggle
            ? isPasswordVisible
                ? "text"
                : "password"
            : props.type
    }, [props.type, isPasswordVisible])

    const needsVisibilityToggle: boolean = useMemo(
        () =>
            props.type === "password" && !props.ignorePasswordVisibilityToggle,

        [props.type, props.ignorePasswordVisibilityToggle],
    )

    const inputHasError = useMemo<boolean>(() => {
        return !!props.reactHookErrors?.[props.name]
    }, [props.reactHookErrors?.[props.name]])

    const inputCss = useMemo<(SerializedStyles | undefined)[]>(() => {
        return [
            props.backgroundColor
                ? css({
                      backgroundColor: props.backgroundColor,

                      // Need to set bg color to nested <input /> as well
                      // Since above bg color is applied to wrapper only
                      input: {
                          backgroundColor: props.backgroundColor,
                      },
                  })
                : undefined,
        ]
    }, [props.backgroundColor])

    const isLabelFloated = useMemo<boolean>(() => {
        return (
            props.isLabelStatic ||
            !!currentInputValue ||
            isFocused ||
            // Handles case when browser autofilled input but user didn't interact with page yet
            (isAutoFilled && typeof currentInputValue === "undefined")
        )
    }, [currentInputValue, isFocused, props.isLabelStatic, isAutoFilled])

    // Set default value in local state if it's different from current
    // Also setValue in form if setValue is passed - with new versions of hook form, control doesn't set default value automatically always - only in initial render
    useEffect(() => {
        props.reactHookSetValue?.(elementFullName, props.defaultValue)

        if (!props.defaultValue) {
            return
        }

        setCurrentInputValue(props.defaultValue)
    }, [props.defaultValue])

    // Disable scroll behavior if input type is number
    useEffect(() => {
        if (!containerRef.current || managedInputType !== "number") {
            return
        }

        const inputElements = containerRef.current.querySelectorAll("input")

        const onInputWheel = (event: WheelEvent) => {
            event.preventDefault()
        }

        inputElements.forEach(input => {
            input.addEventListener("wheel", onInputWheel, { passive: false })
        })

        return () => {
            inputElements.forEach(input => {
                try {
                    input.removeEventListener("wheel", onInputWheel)
                } catch (e) {}
            })
        }
    }, [managedInputType])

    const getPasswordToggleElement = (): React.JSX.Element | undefined =>
        needsVisibilityToggle ? (
            <div
                className="flex items-center justify-center"
                css={[styles.pwdToggleContainer]}
                onClick={() => {
                    if (needsVisibilityToggle) {
                        setIsPasswordVisible(current => !current)
                    }
                }}
            >
                {isPasswordVisible ? (
                    <AiFillEyeInvisible size={24} css={styles.pwdToggleIcon} />
                ) : (
                    <AiFillEye size={24} css={styles.pwdToggleIcon} />
                )}
            </div>
        ) : undefined

    const onInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        originalOnChange?: (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => void,
    ) => {
        // Save current value in local state
        try {
            setCurrentInputValue(event.target.value)
        } catch (e) {}

        if (debouncedOnChange) {
            event.persist() // So it can be used in debounce

            debouncedOnChange(event)
        }

        originalOnChange?.(event)
    }

    return (
        <div
            ref={containerRef}
            className={`
                relative
                ${props.className}
                ${props.label && !isLabelFloated ? "truncate" : ""}
            `}
        >
            {props.label && (
                <FloatingLabelElement
                    label={props.label}
                    labelClassName={props.labelClassName}
                    isFloated={isLabelFloated}
                    withRequiredStar={
                        props.withRequiredStar &&
                        props.reactHookValidations?.required
                    }
                    tooltip={props.tooltip}
                    inputHasPrefix={!!props.inputPrefix}
                />
            )}

            <div className="relative">
                {props.reactHookControl ? (
                    <>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                            }) => {
                                const commonProps = {
                                    value,
                                    name,
                                    ref,
                                    size: props.size || "large",
                                    placeholder:
                                        isLabelFloated || !props.label
                                            ? props.placeholder
                                            : undefined,
                                    prefix: props.inputPrefix,
                                    type: managedInputType,
                                    onChange: (
                                        e: React.ChangeEvent<
                                            | HTMLInputElement
                                            | HTMLTextAreaElement
                                        >,
                                    ) => {
                                        onInputChange(e, onChange)
                                    },
                                    css: inputCss,
                                    className: `${
                                        props.innerInputClassName || ""
                                    } ${
                                        inputHasError &&
                                        (!props.hideErrors ||
                                            props.forceHighlightOnError)
                                            ? "errored"
                                            : ""
                                    }`,
                                    disabled: props.disabled,
                                    allowClear: props.allowClear,
                                    autoFocus: props.autoFocus,
                                    onFocus: () => {
                                        setIsFocused(true)
                                    },
                                    onBlur: () => {
                                        onBlur()

                                        setIsFocused(false)
                                    },
                                    // Password toggle functionality
                                    suffix:
                                        props.inputSuffix ||
                                        getPasswordToggleElement(),
                                }

                                return props.mask ? (
                                    <MaskedInput
                                        {...commonProps}
                                        mask={props.mask}
                                        maskOptions={props.maskOptions}
                                        definitions={props.maskDefinitions}
                                        placeholder={
                                            isLabelFloated || !props.label
                                                ? props.placeholder
                                                : undefined
                                        }
                                    />
                                ) : (
                                    <Input {...commonProps} />
                                )
                            }}
                            defaultValue={props.defaultValue}
                            name={elementFullName}
                            control={props.reactHookControl}
                            rules={props.reactHookValidations}
                            shouldUnregister={
                                typeof props.shouldUnregister !== "undefined"
                                    ? props.shouldUnregister
                                    : true
                            }
                        />

                        {inputHasError &&
                            !props.hideErrors &&
                            props.reactHookErrors && (
                                <InputErrorElement
                                    text={
                                        (
                                            props.reactHookErrors[
                                                props.name
                                            ] as FieldError
                                        ).message || "Error"
                                    }
                                />
                            )}
                    </>
                ) : (
                    (() => {
                        const commonProps = {
                            size: props.size || "large",
                            placeholder:
                                isLabelFloated || !props.label
                                    ? props.placeholder
                                    : undefined,
                            prefix: props.inputPrefix,
                            type: managedInputType,
                            onChange: onInputChange,
                            css: inputCss,
                            className: props.innerInputClassName,
                            disabled: props.disabled,
                            defaultValue: props.defaultValue,
                            allowClear: props.allowClear,
                            onFocus: () => {
                                setIsFocused(true)
                            },
                            onBlur: () => {
                                setIsFocused(false)
                            },
                            // Password toggle functionality
                            suffix:
                                props.inputSuffix || getPasswordToggleElement(),
                        }

                        return props.mask ? (
                            <MaskedInput
                                {...commonProps}
                                mask={props.mask}
                                maskOptions={props.maskOptions}
                                definitions={props.maskDefinitions}
                            />
                        ) : (
                            <Input {...commonProps} />
                        )
                    })()
                )}
            </div>

            {/*Caps lock warning*/}
            {props.type === "password" && isFocused ? (
                <ReactIsCapsLockActive>
                    {(isCapsActive: boolean) =>
                        isCapsActive ? (
                            <PrimaryText
                                typography="caption"
                                css={styles.capsActiveMessage}
                            >
                                Caps Lock is active. Is this desired?
                            </PrimaryText>
                        ) : null
                    }
                </ReactIsCapsLockActive>
            ) : null}

            {/*Password strength indicator*/}
            {props.type === "password" &&
            props.withPasswordStrengthIndicator ? (
                <PasswordStrengthElement
                    css={styles.passwordStrengthContainer}
                    width={props.passwordStrengthWidth}
                    rules={
                        props.passwordStrengthRules ||
                        FRONT_PASSWORD_VALIDATION_CONFIG.rules
                    }
                    currentValue={currentInputValue}
                />
            ) : null}
        </div>
    )
}

const styles = {
    pwdToggleContainer: css({
        height: "100%",
        paddingLeft: 5,
        cursor: "pointer",
    }),
    pwdToggleIcon: css({
        color: NIRVANA_COLORS.brand.primary,
        opacity: 0.5,
    }),
    capsActiveMessage: css({
        marginTop: 5,
    }),
    passwordStrengthContainer: css({
        marginTop: 16,
    }),
}

interface IInputElementProps {
    className?: string
    innerInputClassName?: string
    backgroundColor?: string
    name: string
    fieldGroupName?: string
    label?: string
    isLabelStatic?: boolean
    withRequiredStar?: boolean
    tooltip?: string
    allowClear?: boolean
    debounce?: number // in milliseconds
    reactHookControl?: Control
    reactHookSetValue?: UseFormSetValue<FieldValues>
    reactHookValidations?: IIndexable
    reactHookErrors?: DeepMap<FieldValues, FieldError>
    defaultValue?: any
    placeholder?: string
    disabled?: boolean
    size?: "large" | "small"
    type?:
        | "text"
        | "number"
        | "file"
        | "email"
        | "date"
        | "url"
        | "password"
        | "hidden"
    inputPrefix?: React.ReactNode
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    hideErrors?: boolean
    forceHighlightOnError?: boolean
    ignorePasswordVisibilityToggle?: boolean
    labelClassName?: string
    shouldUnregister?: boolean
    inputSuffix?: React.ReactNode
    mask?: MaskType
    maskOptions?: InputMaskOptions
    maskDefinitions?: InputMaskOptions["definitions"]
    ignoreScrollIntoViewOnError?: boolean
    withPasswordStrengthIndicator?: boolean
    passwordStrengthRules?: IPasswordValidationRule[]
    passwordStrengthWidth?: number
    autoFocus?: boolean
}
